/**
 * @fileoverview DescriptionField component for editing metadata item descriptions.
 * Provides a controlled textarea that can be disabled when linked to existing items.
 */

import { useTranslation } from "@/i18n";

/**
 * Props for the DescriptionField component.
 */
interface DescriptionFieldProps {
  /** Current value of the description field */
  readonly value: string;
  /** Callback when the value changes */
  readonly onChange: (value: string) => void;
  /** Whether the field is disabled (typically when linked to existing item) */
  readonly disabled: boolean;
}

/**
 * DescriptionField provides a textarea for editing metadata item descriptions.
 * The field is disabled when the item is linked to an existing AIIO element.
 *
 * @param props - Component props
 * @returns Description textarea UI
 */
export default function DescriptionField({
  value,
  onChange,
  disabled,
}: DescriptionFieldProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-2">
      <label className="text-sm text-[#18181b] leading-5">{t("documentReview.modal.description")}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`w-full h-32 bg-[#fafafa] rounded-md px-4 py-2 text-sm text-[#71717a] focus:outline-none focus:ring-1 focus:ring-[#d4d4d8] resize-none ${
          disabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
        placeholder={t("documentReview.modal.descriptionPlaceholder")}
      />
    </div>
  );
}
